import React, { useState, useEffect, ReactElement } from 'react';
import { TextField, ScrollBar } from '@framework-controls/core';
import { IView } from 'Framework';
import { strings } from 'src/localization';
import css from './CustSearch.module.scss';
import { Customer, UpdateApptCustInp, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascElementConfigurationInput, ascRetailProcessInput, SpecData } from 'src/models';
import SVPButton from '../SVPButtons/SVPButton';
import { ComponentMapIds } from '../map';
import TextInCircle from 'src/components/TextInCircle/TextInCircle';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Footer from '../Footer/Footer';
import { ascCtaInteraction, ascFormEngagement, ascPageview, ascElementConfiguration, ascRetailProcess } from 'src/helpers/EventHelper/EventHelper';
// import * as Sentry from '@sentry/react';
// import { sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface Iprops {
    rtsToken: string;
    custInfo?: Customer;
    pushToMainPanes: (inputView: IView) => void;
    pushToLeftPane: (inputView: IView) => void;
    customerSearch: (cust: Customer, profile: string, store: string, branch: string) => void;
    onCustUpdate: (cust: Customer) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    errMsg?: string;
    retCode?: string;
    clearError: () => void;
    specProfile: string;
    store: string;
    branch: string;
    fordSmarttFlag: string;
    kiaTrackingFlag: string;
    mainPaneViewOrder: IView[];
    addLogEntry: (action: string, screen: string, details: string) => void;
    toyLexLoginSent: boolean;
    setToyLexLoginSent: (inp) => void;
    multipleCustomers: boolean;
    svcContact?: {num: string | undefined, ext: string | undefined};
    bdyContact?: {num: string | undefined, ext: string | undefined};
    ossContact?: {num: string | undefined, ext: string | undefined};
    oemCustSearchLaunched: boolean;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    gmRewardsEligible: string | undefined,
    gmMemberErr: string,
    myGMRewardsLaunched: string | undefined,
    theme: string;
    spmCustomerCentral: (cust: Customer, profile: string, store: string, branch: string) => void;
    setLoadingStateToIdle: () => void;
    gmMemberSearch: (email: string) => void;
    showGuestApptOption?: string;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source + /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

const alphaNumeric = new RegExp(/[a-zA-Z0-9]/);

const phoneRegex = new RegExp(/^(\d{3})[\s.-]?(\d{3})[\s.-]?(\d{4})$/);
const phoneRegexLong = new RegExp(/^(\d{1})[\s.-]?(\d{3})[\s.-]?(\d{3})[\s.-]?(\d{4})$/);
const phoneRegexShrt = new RegExp(/^(\d{3})[\s.-]?(\d{4})$/);
const phoneRegexShrtCC = new RegExp(/^(\d{1})[\s.-]?(\d{3})[\s.-]?(\d{4})$/);

const CustSearch = (props: Iprops) => {
    const [phoneEmail, setPhoneEmail] = useState('');
    const [phoneEmailError, setPhoneEmailError] = useState(false);
    const [custSearch, setCustSearch] = useState(false);
    const [firstEvent, setFirstEvent] = useState(false);

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    // allow user to try again and go back to search screen
    const tryAgainBtnHandler = (e: any) => {
        props.clearError();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    };

    // is the user closes the error modal, then act as if those chose tyy again.
    const closeHandler = (e: any) => {
        props.clearError();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
    };

    // continue to next screen and use first name that was entered
    const errorContBtnHandler = (e: any) => {
        props.clearError();

        const apptCustInp = {
            nameId: '',
            firstName: '',
            lastName: '',
            emailAddress: phoneEmail.match(emailRegex) ? phoneEmail.trim() : '',
            phoneNumber: phoneEmail.match(phoneRegex) ? phoneEmail.trim() : '',
            login: false,
            prefContact: '',
            textOptIn: '',
            emailOptIn: '',
            streetAddress: '',
            stateAddress: '',
            city: '',
            zipCode: '',
            addVehNameId: '',
            vehicleList: [],
            gmRedeemAmt: '',
            encryptFirst: '',
            encryptLast: '',
            encryptEmail: '',
            encryptPhone: '',
            ascCustId: '',
        };

        props.onAppCustUpdate(apptCustInp);
        props.onCustUpdate(apptCustInp);

        props.pushToMainPanes({
            viewId: ComponentMapIds.AddVehicle,
            component: ComponentMapIds.AddVehicle,
        });
        props.pushToLeftPane({
            viewId: ComponentMapIds.PropertyOptions,
            component: ComponentMapIds.PropertyOptions,
        });
        // clear out text fields in case we go back
        setPhoneEmail('');
        if (apptCustInp.emailAddress && props.myGMRewardsLaunched === 'Y') {
            props.gmMemberSearch(apptCustInp.emailAddress);
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ImNewHere,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.ImNewHere,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    };

    useEffect(() => {
        if (props.loading === 'succeeded' && custSearch) {
            setCustSearch(false);

            // if the user is not found and the OEM customer search is not launched, then we need to display the error
            // if the user is a company name (1005) then we need to display the No Vehicle Access error
            if ((props.retCode === '1004' && !props.oemCustSearchLaunched) || props.retCode === '1005' || props.errMsg === '999') {
                setCustSearch(false);
            } else {
                if (props.oemCustSearchLaunched && props.errMsg) {
                    setPhoneEmail('');
                    setPhoneEmailError(false);

                    props.pushToMainPanes({
                        viewId: ComponentMapIds.OEMCustSearch,
                        component: ComponentMapIds.OEMCustSearch,
                    });
                } else {
                    if (props.custInfo) {
                        const OEMVehicleFound = props.custInfo?.vehicleList?.some(veh => veh.isOEM === 'Y');
                        if (!props.multipleCustomers && !OEMVehicleFound) {
                            const apptCustInp: UpdateApptCustInp = {
                                nameId: props.custInfo?.nameId,
                                firstName: props.custInfo.firstName,
                                lastName: props.custInfo.lastName,
                                emailAddress: props.custInfo.emailAddress,
                                phoneNumber: props.custInfo.phoneNumber,
                                phoneType: props.custInfo.phoneType,
                                prefContact: props.custInfo.prefContact,
                                loggedIn: props.custInfo.login,
                            };
                            props.onAppCustUpdate(apptCustInp);
                        }

                        setPhoneEmail('');
                        setPhoneEmailError(false);

                        props.pushToMainPanes({
                            viewId: ComponentMapIds.VehSel,
                            component: ComponentMapIds.VehSel,
                        });

                        if (isSDAnalyticsEnabled) {
                            sendSdEvent('scheduleServiceLogin', {
                                scheduleServiceSessionId: props.sessionId,
                                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                            });
                        }

                        if (!props.toyLexLoginSent) {
                            props.setToyLexLoginSent(true);
                        }
                    }
                }
            }
            props.setLoadingStateToIdle();
        }
    }, [props.oemCustSearchLaunched, props.custInfo, props.multipleCustomers, props.onAppCustUpdate, props.pushToMainPanes,
        props.errMsg, props.toyLexLoginSent, props.setToyLexLoginSent, props.loading,
        props.setLoadingStateToIdle, custSearch, props.retCode, props.sessionId, isSDAnalyticsEnabled,
        props.toyotaMCLaunched, props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched, props.specData]);

    const emailPhoneChangeHandler = (_: string, value: string) => {
        let formattedValue = '';

        if (phoneEmail === '') {
            const validFirstCharInput = value[0]?.match(alphaNumeric);

            if (!validFirstCharInput) {
                return;
            }
        }

        if (phoneEmail.match(phoneRegex)) {
            formattedValue = value.replace(/[^0-9]/g, '');

            if (formattedValue.length === 11 && formattedValue[0] === '1') {
                formattedValue = formattedValue.slice(1);
            }

            setPhoneEmail(formattedValue);
        } else {
            setPhoneEmail(value);
        }
    };

    const loginHandler = (e: any) => {
        const customerInfo = { ...props.custInfo! };

        if (phoneEmail.match(emailRegex)) {
            customerInfo.emailAddress = phoneEmail;
        }

        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.LogIn,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        props.onCustUpdate(customerInfo);
        props.pushToMainPanes({
            viewId: ComponentMapIds.Login,
            component: ComponentMapIds.Login,
        });
    };

    // If the user selects they are a new customer, then
    // go to the Add Vehicle screen and build the left pane
    // of the Add Vehicle screen.
    const newHandler = (e: any) => {
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ImNew,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '2c',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Continue as Guest',
        };
        ascRetailProcess(ascRetailProcessInp, [], '');
        props.pushToMainPanes({
            viewId: ComponentMapIds.AddVehicle,
            component: ComponentMapIds.AddVehicle,
        });
        props.pushToLeftPane({
            viewId: ComponentMapIds.PropertyOptions,
            component: ComponentMapIds.PropertyOptions,
        });
    };

    const phoneEmailErrorHandler = () => {
        const error = phoneEmail.length > 62 || !(phoneEmail.match(emailRegex) || phoneEmail.match(phoneRegex));
        setPhoneEmailError(error);
        if (firstEvent) {
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'service',
                commType: 'form',
                commStatus: 'start',
                elementText: strings.EmailOrPhone,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'consumer_contact',
            };
            ascFormEngagement(ascFormEngagementInp);
        }
    };
    const searchCustomerHandler = (e: any) => {
        if (!(phoneEmail.match(emailRegex) || phoneEmail.match(phoneRegex))) {
            return;
        }

        let formattedPhoneNumber = '';
        if (phoneEmail.match(phoneRegex)) {
            formattedPhoneNumber = phoneEmail.replace(/[^0-9]/g, '');
        }

        const apptCustInp: UpdateApptCustInp = {
            nameId: '',
            firstName: '',
            lastName: '',
            emailAddress: phoneEmail.match(emailRegex) ? phoneEmail.trim() : '',
            phoneNumber: phoneEmail.match(phoneRegex) ? phoneEmail.trim() : '',
            phoneType: '',
            prefContact: '',
        };
        props.onAppCustUpdate(apptCustInp);

        const custInp: Customer = {
            nameId: '',
            firstName: '',
            lastName: '',
            emailAddress: phoneEmail.match(emailRegex) ? phoneEmail.trim() : '',
            phoneNumber: formattedPhoneNumber,
            login: false,
            prefContact: '',
            textOptIn: '',
            emailOptIn: '',
            streetAddress: '',
            stateAddress: '',
            city: '',
            zipCode: '',
            addVehNameId: '',
            gmRedeemAmt: '',
            encryptFirst: '',
            encryptLast: '',
            encryptEmail: '',
            encryptPhone: '',
            ascCustId: '',
        };

        setCustSearch(true);
        props.customerSearch(custInp, props.specProfile, props.store, props.branch);

        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.LetsGo,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.LetsGo,
            elementType: 'form',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '2a',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Search for Customer',
        };
        ascRetailProcess(ascRetailProcessInp, [], '');
    };

    let errorDialog: ReactElement | null = null;

    // Handle errors given back from the customer search.
    // If the error is 1005 (Company account found AKA No Vehicle Access), then we need to display it regardless of the
    // launch flag for OEM customer search.
    if ((props.retCode || props.errMsg) && props.retCode !== '0000' &&
        props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.CustSearch &&
        (!props.oemCustSearchLaunched || props.retCode === '1005')) {
        const contactPhone : {num: string, ext: string} = {num: '', ext: ''};
        if (props.svcContact?.num) {
            contactPhone.num = props.svcContact.num;
            if (props.svcContact.ext) {
                contactPhone.ext = props.svcContact.ext;
            }
        } else {
            if (props.bdyContact?.num) {
                contactPhone.num = props.bdyContact.num;
                if (props.bdyContact.ext) {
                    contactPhone.ext = props.bdyContact.ext;
                }
            } else {
                if (props.ossContact?.num) {
                    contactPhone.num = props.ossContact?.num;
                    if (props.ossContact.ext) {
                        contactPhone.ext = props.ossContact.ext;
                    }
                }
            }
        }

        errorDialog = GetErrorMessage(props.errMsg!, props.retCode!, tryAgainBtnHandler, errorContBtnHandler, closeHandler,
            phoneEmail, contactPhone);
    }

    const keyFunction = (e) => {
        firstEventHandler();
        if (e.charCode === 13) {
            searchCustomerHandler(e);
        }
    };

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Customer Search', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        if (props.mainPaneViewOrder.length !== 0) {
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.CustSearch) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: '',
                };
                ascPageview(ascPageViewInp);
            }
        }
    }, [props.mainPaneViewOrder]);

    let promptArea: ReactElement | null = null;
    const brandFlags = [
        props.fordSmarttFlag,
        props.kiaTrackingFlag,
        // Add more brand tracking flags here in the future
    ];
    const hasAnyBrandFlag = brandFlags.some(Boolean);

    const textInCircleArea = (!hasAnyBrandFlag || props.showGuestApptOption === 'Y') ?
        <div className={css.circleDiv}>
            <TextInCircle text={strings.OrLiteral} theme={props.theme} />
        </div> : null;

    if (!hasAnyBrandFlag) {
        promptArea = <div className={css.PromptContainer}>
            <div className={css.PromptContent}>
                <h2 className={props.theme === 'mazda' && css.headerMazda}>{strings.MyAccount}</h2>
                <div>{strings.LoginPrompt}</div>
                <div className={css.btnDiv}>
                    <SVPButton
                        id='loginBtn'
                        variant={'primaryBtnAlt2'}
                        message={strings.LogIn}
                        handler={loginHandler}
                        noArrow={true}
                        tooltip={strings.LogInTitle}
                        theme={props.theme}
                    />
                </div>
            </div>
        </div>;
    }

    if (props.showGuestApptOption === 'Y') {
        promptArea = <div className={css.PromptContainer}>
            <div className={css.PromptContent}>
                <h2 className={props.theme === 'mazda' && css.headerMazda}>{strings.ContinueAsGuest}</h2>
                <div>{strings.GuestPrompt}</div>
                <div className={css.btnDiv}>
                    <SVPButton
                        id='loginBtn'
                        variant={'primaryBtnAlt2'}
                        message={strings.ImNew}
                        handler={newHandler}
                        noArrow={true}
                        tooltip={strings.ImNewTip}
                        theme={props.theme}
                    />
                </div>
            </div>
        </div>;
    }

    let formattedValue = phoneEmail;

    const phoneMatch = formattedValue.match(phoneRegex);

    if (phoneMatch) {
        formattedValue = `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
    }

    const isDisabled = phoneEmail.length > 62 || !(phoneEmail.match(emailRegex) || phoneEmail.match(phoneRegex));
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;
    const firstEventHandler = () => {
        setFirstEvent(true);
    };

    return (
        <div>
            <ScrollBar scrollVertical>
                <div className={css.body} onClick={firstEventHandler}>
                    {errorDialog}
                    <h2 className={props.theme === 'mazda' && css.welcomeMazda}>{strings.Welcome}</h2>
                    <h3>{strings.GladtoSee}</h3>
                    <hr />
                    <div className={css.bottomContainer}>
                        <div className={css.InputContainer}>
                            <h2 className={props.theme === 'mazda' && css.headerMazda}>{strings.LetsGetStarted}</h2>
                            <span>{strings.EnterPhoneEmail} {strings.RequiredFields}</span>
                            <div className={css.Input}>
                                <TextField
                                    id='phoneEmail'
                                    label={strings.EmailOrPhone}
                                    value={formattedValue}
                                    size='full'
                                    onChange={emailPhoneChangeHandler}
                                    onBlur={phoneEmailErrorHandler}
                                    onKeyPress={(e) => keyFunction(e)}
                                    type='text'
                                    // error={phoneEmailError}
                                    helperText={phoneEmailError ? strings.ValidEmailPhone : ''}
                                    autoFocus
                                    // raisedLabel={true}
                                />
                            </div>
                            <div className={css.btnDiv}>
                                <SVPButton
                                    id='proceedBtn'
                                    variant={'primaryBtnAlt'}
                                    message={strings.LetsGo}
                                    handler={(e: React.KeyboardEvent<HTMLElement>) => searchCustomerHandler(e)}
                                    isDisabled={isDisabled}
                                    tooltip={strings.ProceedTip}
                                    theme={props.theme}
                                    noArrow={noArrow}
                                />
                            </div>
                        </div>
                        {textInCircleArea}
                        {promptArea}
                    </div>
                </div>
            </ScrollBar>
            <Footer
                id='CustSearchFooter'
                handler={() => null}
                message={strings.Confirm}
                secondaryhandler={() => null}
                noButtons
            />
        </div>
    );
};

function GetErrorMessage(
    message: string,
    retCode: string,
    tryAgainHandler: (e: any) => void,
    continueHandler: (e: any) => void,
    closeHandler: (e: any) => void,
    searchField: string,
    contactPhone: {num: string, ext: string},
) {
    let headerMsg = '';
    let errorMsg = '';
    let primaryMessage = '';
    let secondaryMessage = '';
    let primaryHandler = tryAgainHandler;
    let secondaryHandler = continueHandler;
    let formattedValue = searchField;
    let noVehAccessCntct = contactPhone.num;
    const phoneMatch = searchField.match(phoneRegex);
    let noVehContactMatch = noVehAccessCntct?.match(phoneRegex);

    if (phoneMatch) {
        formattedValue = `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
    }

    if (noVehContactMatch) {
        noVehAccessCntct = `(${noVehContactMatch[1]}) ${noVehContactMatch[2]}-${noVehContactMatch[3]}`;
    }

    noVehContactMatch = noVehAccessCntct?.match(phoneRegexLong);
    if (noVehContactMatch) {
        noVehAccessCntct = `(+${noVehContactMatch[1]}) ${noVehContactMatch[2]}-${noVehContactMatch[3]}-${noVehContactMatch[4]}`;
    }

    noVehContactMatch = noVehAccessCntct?.match(phoneRegexShrt);
    if (noVehContactMatch) {
        noVehAccessCntct = `${noVehContactMatch[1]}-${noVehContactMatch[2]}`;
    }

    noVehContactMatch = noVehAccessCntct?.match(phoneRegexShrtCC);
    if (noVehContactMatch) {
        noVehAccessCntct = `(+${noVehContactMatch[1]}) ${noVehContactMatch[2]}-${noVehContactMatch[3]}`;
    }

    if (contactPhone.ext) {
        noVehAccessCntct += ` ext. ${contactPhone.ext}`;
    }

    if (message) {
        headerMsg = strings.InvalidCode;
        errorMsg = message;
        primaryMessage = strings.TryAgain;
        primaryHandler = tryAgainHandler;
    }

    if (retCode === '1004' || message === '999') {
        headerMsg = strings.DidNotFindYou;
        errorMsg = strings.formatString(strings.CustSearchErrorMessage, formattedValue).toString();
        primaryMessage = strings.TryAgain;
        secondaryMessage = strings.ImNewHere;
        primaryHandler = tryAgainHandler;
        secondaryHandler = continueHandler;
    }

    if (retCode === '1005') {
        headerMsg = strings.NoVehAccess;
        errorMsg = strings.formatString(strings.CompanyAccFound, formattedValue, noVehAccessCntct).toString();
        primaryMessage = strings.Continue;
        secondaryMessage = strings.Cancel;
        primaryHandler = continueHandler;
        secondaryHandler = tryAgainHandler;
    }

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage}
            primaryHandler={primaryHandler}
            secondaryHandler={secondaryHandler}
            closeHandler={closeHandler}
        />
    );
}

export default CustSearch;
